<script setup lang="ts">
import { ref, watch, onUnmounted, type Component } from 'vue';
import { useI18n } from 'vue-i18n';

interface MenuItem {
    icon: Component;
    path: string;
    name: string;
    permission?: string;
}
defineProps<{ menus: MenuItem[]; wide: boolean }>();
const emit = defineEmits<{ select: [menu: MenuItem] }>();

const { t } = useI18n();
const rootRef = ref<HTMLElement | null>(null);
const showDropdown = ref(false);
let hideTimeout: number | null = null;
// touch has no real hover: mouseenter won't refire on a repeat tap of the same button, so it must toggle via click instead
const isTouch = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// clicking anywhere outside the component closes the dropdown
const handleOutsideClick = (e: MouseEvent) => { if (rootRef.value && !rootRef.value.contains(e.target as Node)) showDropdown.value = false; };
watch(showDropdown, (open) => open ? document.addEventListener('click', handleOutsideClick) : document.removeEventListener('click', handleOutsideClick));

const handleButtonEnter = () => {
    if (hideTimeout) { clearTimeout(hideTimeout); hideTimeout = null; }
    showDropdown.value = true;
};
const handleButtonLeave = () => {
    hideTimeout = window.setTimeout(() => {
        if (!showDropdown.value) return;
        showDropdown.value = false;
    }, 300);
};
const handleButtonClick = () => { showDropdown.value = !showDropdown.value; };
const handleDropdownEnter = () => {
    if (hideTimeout) { clearTimeout(hideTimeout); hideTimeout = null; }
    showDropdown.value = true;
};
const handleDropdownLeave = () => { showDropdown.value = false; };

const onMenuClick = (menu: MenuItem) => {
    showDropdown.value = false;
    emit('select', menu);
};

onUnmounted(() => {
    if (hideTimeout) { clearTimeout(hideTimeout); hideTimeout = null; }
    document.removeEventListener('click', handleOutsideClick);
});
</script>

<template>
    <li ref="rootRef" class="text-xs text-center w-full cursor-pointer text-white relative h-[30px] flex items-center justify-center hover:bg-white/10"
        v-on="isTouch ? { click: handleButtonClick } : { mouseenter: handleButtonEnter, mouseleave: handleButtonLeave }">
        <div class="flex items-center gap-1.5">
            <div class="w-1.5 h-1.5 rounded-full bg-white"></div>
            <div class="w-1.5 h-1.5 rounded-full bg-white"></div>
            <div class="w-1.5 h-1.5 rounded-full bg-white"></div>
        </div>
        <div v-show="showDropdown" class="fixed top-1/2 -translate-y-1/2 ml-2 z-50"
            :style="{ left: wide ? '200px' : '77px' }">
            <div class="absolute right-full top-0 h-full w-2"></div>
            <div class="bg-[#55599A] rounded-[10px] py-2 px-2 flex items-center gap-4"
                @mouseenter="handleDropdownEnter" @mouseleave="handleDropdownLeave">
                <div v-for="menu in menus" :key="menu.path"
                    class="flex flex-col items-center justify-center cursor-pointer hover:bg-white rounded-lg px-4 py-3 text-white hover:text-[#55599A] transition-colors min-w-[100px]"
                    @click.stop="onMenuClick(menu)">
                    <el-icon size="30" class="mb-1 group-hover:text-[#55599A]">
                        <component :is="menu.icon"></component>
                    </el-icon>
                    <span class="text-xs whitespace-nowrap">{{ t(menu.name) }}</span>
                </div>
            </div>
        </div>
    </li>
</template>
